import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './Header.css';

const Header = ({ userRole }) => {
  const { currentUser, userProfile, hotelStatus, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Check if user is owner with pending/declined status
  const isPendingOwner = userProfile?.role === 'owner' && 
    (hotelStatus?.status === 'pending' || hotelStatus?.status === 'declined' || hotelStatus?.status === 'not_registered');

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      setShowUserMenu(false);
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const navigateFromMenu = (path) => {
    navigate(path);
    setShowUserMenu(false);
  };

  const handleDashboard = () => {
    // Use prop userRole if available, otherwise fallback to userProfile
    const role = userRole || userProfile?.role;
    
    // Route to appropriate dashboard based on user role
    let dashboardPath = '/guest'; // default for guest
    
    if (role === 'admin') {
      dashboardPath = '/admin';
    } else if (role === 'manager') {
      dashboardPath = '/manager';
    } else if (role === 'owner') {
      dashboardPath = '/owner';
    }
    
    navigateFromMenu(dashboardPath);
  };

  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          {/* Logo */}
          <div className="logo" onClick={() => navigate('/')}>
            <div className="logo-icon">IB</div>
            <span className="logo-text">InnBotics</span>
          </div>

          {/* Navigation Links */}
          <nav className={`nav ${showMobileMenu ? 'nav-open' : ''}`}>
            {/* ...existing code... */}
          </nav>

          {/* User Actions */}
          <div className="user-actions">
            {currentUser ? (
              <div className="user-menu">
                <button 
                  className="user-button"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 16px',
                    border: '2px solid #003580',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <div className="user-avatar" style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#003580',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}>
                    {currentUser.photoURL ? (
                      <img src={currentUser.photoURL} alt="Profile" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                    ) : (
                      <span>{currentUser.email?.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <span className="user-name" style={{ color: '#003580' }}>
                    {currentUser.displayName || currentUser.email?.split('@')[0]}
                  </span>
                  <span className="dropdown-arrow" style={{ color: '#003580', fontSize: '10px' }}>▼</span>
                </button>

                {showUserMenu && (
                  <div className="user-dropdown">
                    <div className="user-info">
                      <div className="user-email">{currentUser.email}</div>
                      {isPendingOwner && hotelStatus && (
                        <div className="user-status" style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          marginTop: '4px',
                          background: hotelStatus.status === 'pending' ? '#fef3c7' : '#fee2e2',
                          color: hotelStatus.status === 'pending' ? '#92400e' : '#991b1b'
                        }}>
                          {hotelStatus.status === 'pending' ? '⏳ Registration Pending' : 
                           hotelStatus.status === 'declined' ? '⚠️ Registration Declined' : 
                           '📝 Not Registered'}
                        </div>
                      )}
                    </div>
                    <div className="dropdown-divider"></div>
                    
                    {/* Simplified menu showing only Dashboard and Sign Out */}
                    <button onClick={handleDashboard} className="dropdown-item" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      fontSize: '15px',
                      fontWeight: '500'
                    }}>
                      <span className="item-icon" style={{ fontSize: '20px' }}>🏠</span>
                      Dashboard
                    </button>
                    
                    <div className="dropdown-divider"></div>
                    <button onClick={handleLogout} className="dropdown-item logout" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#d32f2f'
                    }}>
                      <span className="item-icon" style={{ fontSize: '20px' }}>🚪</span>
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-buttons">
                <button 
                  onClick={() => navigate('/signup')} 
                  className="signup-btn"
                >
                  Register
                </button>
                <button 
                  onClick={handleSignIn} 
                  className="signin-btn"
                >
                  Sign In
                </button>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button 
              className="mobile-menu-toggle"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Overlay */}
      {showMobileMenu && (
        <div className="mobile-nav-overlay" onClick={() => setShowMobileMenu(false)}>
          <div className="mobile-nav">
            {currentUser && (
              <>
                <button type="button" className="mobile-nav-link" onClick={handleDashboard}>
                  🏠 Dashboard
                </button>
                <div className="mobile-nav-divider"></div>
              </>
            )}
            {!currentUser && (
              <>
                <div className="mobile-nav-divider"></div>
                <button onClick={() => navigate('/login')} className="mobile-nav-link">
                  Sign In
                </button>
                <button onClick={() => navigate('/signup')} className="mobile-nav-link">
                  Register
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
